import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Switch,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { loadPartitions, loadBuildings } from "../../../../utils/buildingPartitionHelpers";
import AppointmentsApi from "../../../../Classes/API/Appointments/AppointmentsApi";
import toast from "react-hot-toast";

const ChangeSelectionModal = ({
  open,
  onClose,
  AccSession,
  AppHour,
  AppDay,
  GetAppDayById,
  type, // 'building' or 'partition'
}) => {
  const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const isBuilding = type === "building";
  const title = isBuilding ? "Change Building" : "Change Partition";
  const label = isBuilding ? "Select Building" : "Select Partition";
  const toggleLabel = isBuilding ? "Show Inactive Buildings" : "Show Inactive Partitions";

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const data = isBuilding 
        ? await loadBuildings("", showInactive)
        : await loadPartitions("", showInactive);

      setOptions(data.map(item => ({
        id: item.value,
        name: item.label,
        isActive: item.isActive
      })));
    } catch (error) {
      console.error(`Error fetching ${type}s:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchOptions();
      setSelectedValue("");
    }
  }, [open, showInactive]);

  const handleUpdate = async () => {
    if (!selectedValue) {
      toast.error(`Please select a ${type}`);
      return;
    }

    setUpdating(true);
    try {
      const req = {
        accountingSessionId: AccSession.id,
        appointmentHourId: AppHour.id,
        [isBuilding ? "newBuildingId" : "newPartitionId"]: parseInt(selectedValue),
      };

      const res = isBuilding 
        ? await AppointmentsApi.UpdateAppointmentBuilding(req)
        : await AppointmentsApi.UpdateAppointmentPartition(req);

      if (res.status === "Success") {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} updated successfully.`);
        if (GetAppDayById) GetAppDayById(AppDay.id);
        onClose();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message || `Failed to update ${type}`);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <MDBox mt={3}>
          <MDInput
            select
            fullWidth
            label={label}
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            disabled={loading || updating}
            SelectProps={{
              sx: { height: "45px" },
            }}
          >
            <MenuItem value="">
              <em>{label}</em>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name} {!option.isActive ? "(Inactive)" : ""}
              </MenuItem>
            ))}
          </MDInput>
        </MDBox>
        <MDBox mt={2} display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="button" color="text" sx={{ cursor: "pointer" }}>
            {toggleLabel}
          </MDTypography>
          <Switch
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            color="info"
          />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary" variant="text" disabled={updating}>
          Cancel
        </MDButton>
        <MDButton
          onClick={handleUpdate}
          color="info"
          variant="gradient"
          disabled={updating || !selectedValue}
        >
          {updating ? <CircularProgress size={20} color="inherit" /> : "Update"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeSelectionModal;
